import { useEffect, useState } from 'react';

import { SecurityBlockIcon, SecurityCheckIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import {
  Button,
  FormMessage,
  Input,
  InputGroup,
  MessageType,
  Select,
  ToggleSwitch,
} from 'components/FormComponents';

import {
  disableSSOOrganization,
  getSsoConfig,
  getSsoStatus,
  selectCompany,
  updateCompany,
} from 'api/company-slice';
import { getRoles, selectRoles } from 'api/roles-slice';

export const SingleSignOnConfig = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const company = useSelector(selectCompany);
  const roles = useSelector(selectRoles);

  const [enabled, setEnabled] = useState(!!company?.custom_domain);
  const [domain, setDomain] = useState(company?.custom_domain);
  const [autoProvision, setAutoProvision] = useState(!!company?.sso_auto_provision);
  const [defaultRoleId, setDefaultRoleId] = useState(company?.sso_default_role_id);
  const [confirmEnable, setConfirmEnable] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);

  useEffect(() => {
    dispatch(getRoles());
    if (company?.custom_domain) dispatch(getSsoStatus());
  }, []);

  const onToggle = e => {
    if (e.target.checked) {
      setEnabled(true);
    } else if (company?.custom_domain) {
      setConfirmDisable(true);
    } else {
      setEnabled(false);
    }
  };

  const onDisable = async () => {
    setConfirmDisable(false);
    setEnabled(false);
    await dispatch(disableSSOOrganization());
  };

  const onAutoProvisionToggle = async e => {
    const checked = e.target.checked;
    setAutoProvision(checked);
    await dispatch(updateCompany({ id: company.id, params: { sso_auto_provision: checked } }));
  };

  const onDefaultRoleChange = async e => {
    const roleId = e.target.value;
    setDefaultRoleId(roleId);
    await dispatch(updateCompany({ id: company.id, params: { sso_default_role_id: roleId } }));
  };

  const openConfigurationPortal = async () => {
    const { payload } = await dispatch(getSsoConfig(domain));
    window.open(payload.portal_link);
  };

  const onConfigure = () => {
    if (company?.custom_domain) {
      openConfigurationPortal();
    } else {
      setConfirmEnable(true);
    }
  };

  const onEnable = () => {
    setConfirmEnable(false);
    openConfigurationPortal();
  };

  return (
    <>
      <Alert
        show={confirmEnable}
        title={t('company.tabs.authenticationSecurity.sso.enableTitle', { domain })}
        message={t('company.tabs.authenticationSecurity.sso.enableMessage')}
        ok={t('company.tabs.authenticationSecurity.sso.enableConfirm')}
        cancel={t('common.cancel')}
        icon={SecurityCheckIcon}
        type="warning"
        onOk={onEnable}
        onCancel={() => setConfirmEnable(false)}
      />
      <Alert
        show={confirmDisable}
        title={t('company.tabs.authenticationSecurity.sso.disableTitle')}
        message={t('company.tabs.authenticationSecurity.sso.disableMessage')}
        ok={t('company.tabs.authenticationSecurity.sso.disableConfirm')}
        cancel={t('common.cancel')}
        icon={SecurityBlockIcon}
        type="error"
        showConfirmationInput
        onOk={onDisable}
        onCancel={() => setConfirmDisable(false)}
      />
      <div className={cn('background-secondary', 'u-padding')}>
        <div className={cn('card', 'card-with-border', 'u-padding')}>
          <EditableSettings
            title="Single Sign-On Enabled"
            name="sso_organization_id"
            open={enabled}
            toggleable
            onChange={onToggle}>
            <div>
              {company?.custom_domain && (
                <FormMessage
                  type={company.sso_enabled ? MessageType.Success : MessageType.Info}
                  message={t(
                    company.sso_enabled
                      ? 'company.tabs.authenticationSecurity.sso.statusActive'
                      : 'company.tabs.authenticationSecurity.sso.statusPending',
                  )}
                  closeable={false}
                  className="u-margin-top--0"
                />
              )}
              <InputGroup>
                <Input
                  name="custom_domain"
                  placeholder="checkmate.tech"
                  disabled={!!company?.custom_domain}
                  inputProps={{ value: domain, onChange: e => setDomain(e.target.value) }}
                />
              </InputGroup>
              <ToggleSwitch
                className="u-margin-top u-margin-0"
                label="Automatically provision new users"
                tooltip="Anyone who successfully signs in through Single Sign-On gets a Checkmate account created automatically if one doesn't already exist. Turn this off to only allow sign-in for users who already have a Checkmate account."
                inputProps={{ checked: autoProvision, onChange: onAutoProvisionToggle }}
              />
              {autoProvision && (
                <InputGroup
                  className="u-margin-top u-margin-bottom--0"
                  title={t('company.tabs.authenticationSecurity.sso.defaultRoleTitle')}
                  description={t('company.tabs.authenticationSecurity.sso.defaultRoleDescription')}>
                  <Select
                    name="sso_default_role_id"
                    value={defaultRoleId}
                    onChange={onDefaultRoleChange}>
                    {roles.map(role => (
                      <Select.Item key={role.id} value={role.id}>
                        {role.name}
                      </Select.Item>
                    ))}
                  </Select>
                </InputGroup>
              )}
            </div>
          </EditableSettings>
        </div>
      </div>
      <div className={cn('card_footer', 'u-flex-box', 'u-flex-justify-end')}>
        <Button onClick={onConfigure} disabled={!enabled || !domain}>
          Configure
        </Button>
      </div>
    </>
  );
};
